import { Stack } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SaveCertificatePayload } from "Services/api/register/interfaces";
import { CertificateLimitsResponse } from "Services/api/request/interfaces";
import { getCertificateLimits, getProductRequests, saveCertificateRequest } from "Services/api/request/request";
import {
  CertificateForm,
  CertificateFormSkeleton,
  useCertificateFormSchema,
} from "Shared/CertificateForm/CertificateForm";
import FormikForm from "Shared/FormikForm/FormikForm";
import FormikSubmitButton from "Shared/FormikSubmitButton/FormikSubmitButton";
import { Query } from "Shared/Query/Query";
import { SectionTitle } from "Shared/styled";
import { requiredValidation } from "Utils/validations";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { createSearchParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";

export function CertificateRequest() {
  const { enqueueSnackbar } = useSnackbar();

  const result = useQuery({
    queryKey: [getCertificateLimits.name],
    queryFn: async () => {
      try {
        const response = await getCertificateLimits();
        return response.data;
      } catch (error) {
        enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
        console.error(error);
        throw error;
      }
    },
  });

  return (
    <Stack>
      <SectionTitle variant="h1">Solicitud de apertura de certificado</SectionTitle>
      <Query
        result={result}
        OnLoading={() => <CertificateFormSkeleton />}
        onError={() => <>Ha ocurrido un error al intentar obtener las opciones del formulario</>}
        onSuccess={(optionsResult) => <Form result={optionsResult} />}
      />
    </Stack>
  );
}

interface FormProps {
  result: CertificateLimitsResponse["data"];
}

function Form({ result }: FormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { minTimeLimit, maxTimeLimit } = result;
  const { enqueueSnackbar } = useSnackbar();
  const [minTerm] = [minTimeLimit, maxTimeLimit];

  const schema = useCertificateFormSchema({ options: result });

  const initialValues: SaveCertificatePayload = {
    amount: null,
    term: minTerm,
    liquidation: "",
    typeofPayment: "",
    savingsAccount: "",
    account: "",
    accountNumber: "",
    accountBank: "",
    accountType: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const requestData = new FormData();

          for (const key in values) {
            const el = values[key as keyof SaveCertificatePayload];
            if (el) requestData.append(key, typeof el === "number" ? el.toString() : el);
          }
          const { data } = await saveCertificateRequest(requestData);
          enqueueSnackbar("Solicitud creada exitosamente", { variant: "success" });

          void queryClient.invalidateQueries({
            queryKey: [getProductRequests.name],
            exact: true,
          });

          if (data.signatureRequired)
            navigate({
              pathname: "/solicitudes/firma/",
              search: createSearchParams({
                id: data.uniqueId,
                type: data.type,
              }).toString(),
            });
          else navigate("/solicitudes/estados/");

          setSubmitting(false);
        } catch (error) {
          enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
        }
      }}
      validationSchema={schema.concat(
        Yup.object({
          savingsAccount: Yup.string().when("typeofPayment", {
            is: "withdrawable",
            then: () => requiredValidation,
          }),
        })
      )}
    >
      <FormikForm width="100%">
        <CertificateForm options={result} />
        <FormikSubmitButton fullWidth variant="contained">
          Siguiente
        </FormikSubmitButton>
      </FormikForm>
    </Formik>
  );
}
